import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Navigation = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const menuItems = [
    { name: 'Home', id: 'hero', path: '/' },
    { name: 'About', id: 'about', path: '/' },
    { name: 'Services', id: 'services', path: '/services', isPage: true },
    { name: 'Live Demos', id: 'live-demos', path: '/live-demos', isPage: true },
    { name: 'Why NYAXO', id: 'why-nyaxo', path: '/' },
    { name: 'Results', id: 'case-studies', path: '/' },
    { name: 'Contact', id: 'contact', path: '/' }
  ];

  const handleMenuClick = (item) => {
    if (item.isPage) {
      navigate(item.path);
      window.scrollTo(0, 0);
    } else {
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => scrollToSection(item.id), 100);
      } else {
        scrollToSection(item.id);
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#0D0D0D]/95 backdrop-blur-md shadow-lg border-b border-[#2F2F2F]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => scrollToSection('hero')}
            data-testid="nav-logo"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_find-your-agent/artifacts/m94nk0ss_NYAXO.png"
              alt="NYAXO Logo"
              className="h-14 md:h-16"
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {menuItems.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => handleMenuClick(item)}
                className="text-white hover:text-[#00E600] transition-colors duration-300 font-semibold uppercase tracking-wide text-sm relative group"
              >
                {item.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#00E600] to-[#36E0A8] group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <div className="hidden md:block">
            <button
              data-testid="nav-cta-button"
              onClick={() => scrollToSection('contact')}
              className="px-6 py-2 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_20px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide text-sm"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white focus:outline-none"
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden mt-4 pb-4 space-y-3 border-t border-[#2F2F2F] pt-4"
          >
            {menuItems.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => handleMenuClick(item)}
                className="block w-full text-left text-white hover:text-[#00E600] transition-colors duration-300 font-semibold uppercase tracking-wide text-sm py-2"
              >
                {item.name}
              </button>
            ))}
            <button
              data-testid="mobile-nav-cta"
              onClick={() => scrollToSection('contact')}
              className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_20px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide text-sm"
            >
              Get Started
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;